import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { AuthContext } from "../Auth/AuthContext";
import {
  listConversations,
  createConversation as apiCreate,
  deleteConversation as apiDelete,
} from "../../services/chatApi";

// Estado compartilhado do assistente: a lista de conversas vive no rail (menu
// principal) e o thread vive na página de chat. Ambos consomem este contexto.
const ChatContext = createContext({
  conversations: [],
  activeId: null,
  reload: async () => {},
  newConversation: () => {},
  selectConversation: () => {},
  removeConversation: async () => {},
  upsertConversation: () => {},
  createConversation: async () => {},
});

export const ChatProvider = ({ children }) => {
  const { isAuth } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);

  // a conversa ativa vive na URL (/chat/:id) — assim o refresh volta para ela
  const match = location.pathname.match(/^\/chat\/(\d+)/);
  const activeId = match ? Number(match[1]) : null;

  const reload = useCallback(async () => {
    try {
      const data = await listConversations();
      setConversations(data);
      return data;
    } catch (e) {
      return [];
    }
  }, []);

  useEffect(() => {
    if (!isAuth) {
      setConversations([]);
      return;
    }
    reload();
  }, [isAuth, reload]);

  // "Nova conversa": vai para o chat sem id (estado inicial/hero)
  const newConversation = useCallback(() => {
    history.push("/chat");
  }, [history]);

  const selectConversation = useCallback(
    id => {
      history.push(`/chat/${id}`);
    },
    [history]
  );

  const removeConversation = useCallback(
    async id => {
      await apiDelete(id);
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeId === id) history.push("/chat");
    },
    [activeId, history]
  );

  // cria/atualiza uma conversa e move para o topo (usado pela página ao criar
  // a 1ª mensagem e ao receber o título auto-gerado)
  const upsertConversation = useCallback(conv => {
    setConversations(prev => {
      const rest = prev.filter(c => c.id !== conv.id);
      const existing = prev.find(c => c.id === conv.id);
      return [{ ...existing, ...conv }, ...rest];
    });
  }, []);

  // helper usado pela página para criar a conversa no envio inicial; reflete o
  // id na URL (replace) para o refresh manter a conversa
  const createConversation = useCallback(
    async greeting => {
      const conv = await apiCreate(greeting);
      setConversations(prev => [conv, ...prev]);
      history.replace(`/chat/${conv.id}`);
      return conv;
    },
    [history]
  );

  return (
    <ChatContext.Provider
      value={{
        conversations,
        activeId,
        reload,
        newConversation,
        selectConversation,
        removeConversation,
        upsertConversation,
        createConversation,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);

export { ChatContext };
